import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { SidebarModule } from 'primeng/sidebar';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayer } from '../../../models/data-layer';
import { DataLayerType } from '../../../models/data-layer-type';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { FeatureDropdownComponent } from '../../atoms/feature-dropdown/feature-dropdown.component';
import { CategoryStore } from '../../../stores/category/category.state';
import { IndicatorData } from '../../../models/indicator-data';
import { IndicatorTreeHelperService } from '../../../indicator-tree-helper.service';
import { IndicatorTreeNode } from '../../../models/IndicatorTreeNode';
import { TreeModule } from 'primeng/tree';
import { TreeTableModule } from 'primeng/treetable';
import { InputTextModule } from 'primeng/inputtext';
import { ValueComparisonVisualComponent } from '../../atoms/value-comparison-visual/value-comparison-visual.component';
import { UnitComponent } from '../../atoms/unit/unit.component';
import { FormsModule } from '@angular/forms';
import { GlobalsStore } from '../../../stores/globals.state';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { TypeDropdownComponent } from '../../atoms/type-dropdown/type-dropdown.component';
import { LayerDropdownComponent } from '../../atoms/layer-dropdown/layer-dropdown.component';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { Button } from 'primeng/button';
import { ExportSheet } from '../../../services/data/export-helper.service';
import { sanitizeFilename } from '../../../utility/app.utility';

@Component({
  selector: 'app-district-report',
  standalone: true,
  imports: [
    SidebarModule,
    HeaderComponent,
    FeatureDropdownComponent,
    TreeModule,
    TreeTableModule,
    InputTextModule,
    ValueComparisonVisualComponent,
    UnitComponent,
    FormsModule,
    TypeDropdownComponent,
    LayerDropdownComponent,
    AlertComponent,
    Button,
  ],
  templateUrl: './district-report.component.html',
  styleUrl: './district-report.component.scss',
})
export class DistrictReportComponent implements OnInit, OnDestroy {
  readonly globalsStore = inject(GlobalsStore);
  readonly layerStore = inject(DataLayerStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly geoStore = inject(GeoDataStore);
  readonly comparisonValueStore = inject(ComparisonValueStore);

  @Input() dataLayerId?: string;
  @Input() districtKey?: string;
  dataLayer: DataLayer | null = null;
  dataLayerType: DataLayerType | null = null;
  availableData: IndicatorData[] = [];
  indicatorIds: string[] = [];
  tree: IndicatorTreeNode[] = [];
  data: any;

  constructor(private treeHelper: IndicatorTreeHelperService) {}

  ngOnInit() {
    this.init();
  }

  /**
   * Generates a report title based on the current data layer type, year, and district information.
   *
   * @return {string} The generated report title. Defaults to 'Gebietsbericht' if no title can be constructed from available data.
   */
  getReportTitle(): string {
    let title = '';
    if (this.dataLayerType && this.dataLayerType.name) {
      title += this.dataLayerType.name;
    }

    if (this.dataLayer && this.dataLayer.year) {
      title += ` ${this.dataLayer.year}`;

      if (this.districtKey && this.dataLayer.geoData) {
        const district = this.geoStore.getDistrictByIdAndKey(
          this.dataLayer.geoData,
          this.districtKey
        );
        if (district) title += ` ${district.name}`;
      }
    }

    return title || 'Gebietsbericht';
  }

  /**
   * Initializes the report by setting up the necessary data layer and type, and loading the report.
   * Checks for the presence of a dataLayerId, retrieves the corresponding data layer and type,
   * and then proceeds to load the report.
   *
   * @return {void} Does not return a value.
   */
  init(): void {
    if (this.dataLayerId) {
      this.dataLayer = this.layerStore.getById(this.dataLayerId);
      if (this.dataLayer) {
        this.dataLayerType = this.layerTypeStore.getById(this.dataLayer.parent);
      }
      this.loadReport();
    }
  }

  /**
   * Loads the report based on the current data layer and configurations.
   * The method initializes, processes, and organizes data required for report generation.
   * It also sets up default values for specific keys if they are undefined.
   *
   * @return {void} This method does not return a value.
   */
  loadReport(): void {
    if (!this.dataLayer || !this.dataLayerType || !this.dataLayer.geoData)
      return;
    this.setAvailableData();

    const data = this.dataStore.groupByLayerAndIndicator();

    if (!this.dataLayerId) return;
    this.data = data[this.dataLayerId];
    if (!this.districtKey && this.data) {
      const firstDistrict = this.geoStore.getFirstDistrictById(
        this.dataLayer.geoData
      );

      if (firstDistrict) {
        this.districtKey = firstDistrict.key;
      } else {
        this.districtKey = undefined;
      }
    }
    this.getGroupedTreeStructure();
  }

  /**
   * Updates the district key and reloads the report data.
   *
   * @param {string} districtKey - The new district key to be set.
   * @return {void}
   */
  changeDistrictKey(districtKey: string): void {
    this.districtKey = districtKey;
    this.loadReport();
  }

  /**
   * Constructs and organizes a tree structure by merging categories and indicators.
   * This method groups indicators by their respective categories, processes the initial category tree structure,
   * filters categories with associated indicators, and merges the indicators into the final tree structure.
   *
   * @return {void} Does not return a value but generates a grouped and merged tree structure
   *                 containing categories and indicators, which is stored in the component state.
   */
  getGroupedTreeStructure(): void {
    const groupedByCategory = this.indicatorStore.groupedByCategory();
    const initialCategoryTree = this.categoryStore.asTree();
    if (initialCategoryTree && initialCategoryTree.length) {
      setTimeout(() => {
        const filteredCategoryTree =
          this.treeHelper.filterCategoriesWithIndicators(
            initialCategoryTree,
            groupedByCategory
          );

        this.tree = this.treeHelper.mergeIndicatorsWithCategoryTree(
          filteredCategoryTree,
          groupedByCategory,
          true
        );
      });
    }
  }

  /**
   * Sets the available data based on the current data layer type and ID.
   * Updates the indicator IDs and applies the filter to the indicator store.
   *
   * @return {void} This method does not return any value.
   */
  setAvailableData(): void {
    if (this.dataLayerType && this.dataLayerId) {
      this.availableData = this.dataStore.filterByDataLayer(this.dataLayerId);

      const indicatorIdsWithoutSets = this.availableData.map(
        item => item.indicator
      );

      this.indicatorIds = indicatorIdsWithoutSets.concat(
        this.indicatorStore.getSetIdsByIds(indicatorIdsWithoutSets)
      );

      this.indicatorStore.updateFilter({
        query: '',
        ids: this.indicatorIds,
        locked: null,
        published: null,
        category: [],
        tags: [],
        products: [],
      });
    }
  }

  /**
   * Retrieves the average value for a given indicator ID.
   *
   * @param {string} indicatorId - The ID of the indicator for which the average value is to be retrieved.
   * @return {number | null} The average value associated with the provided indicator ID, or null if the data is unavailable.
   */
  getAvgValue(indicatorId: string): number | null {
    if (!this.data) return null;
    return this.data[indicatorId]?.avg || null;
  }

  /**
   * Retrieves the value associated with the given indicator ID for the current district key.
   *
   * @param {string} indicatorId - The ID of the indicator whose value is to be retrieved.
   * @return {number | null} The value for the specified indicator ID and district key, or null if not available.
   */
  getValue(indicatorId: string): number | null {
    if (!this.districtKey || !this.data) return null;
    return this.data[indicatorId]?.data[this.districtKey] || null;
  }

  /**
   * Retrieves the minimum value for a given layer and indicator from the data store.
   *
   * @param {string} layerId - The unique identifier of the layer.
   * @param {string} indicatorId - The unique identifier of the indicator.
   * @return {number | null} The minimum value associated with the provided layer and indicator.
   */
  getMinValue(layerId: string, indicatorId: string): number | null {
    return this.dataStore.getMinValueByLayerAndIndicator(
      layerId,
      indicatorId,
      this.comparisonValueStore.keys()
    );
  }

  /**
   * Retrieves the maximum value for a given layer and indicator based on the comparison value store.
   *
   * @param {string} layerId - The identifier for the layer to retrieve the maximum value from.
   * @param {string} indicatorId - The identifier of the indicator used for fetching the maximum value.
   * @return {number | null} The maximum value corresponding to the specified layer and indicator, or undefined if not found.
   */
  getMaxValue(layerId: string, indicatorId: string): number | null {
    return this.dataStore.getMaxValueByLayerAndIndicator(
      layerId,
      indicatorId,
      this.comparisonValueStore.keys()
    );
  }

  /**
   * Retrieves a formatted string representation of the given value based on the provided indicator ID.
   *
   * @param {number | null} value - The numeric value to be formatted. Can be null.
   * @param {string} indicatorId - The identifier used to determine the formatting rules.
   * @return {string} The formatted string representation of the value.
   */
  getValueFormatted(value: number | null, indicatorId: string): string {
    return this.globalsStore.getFormattedValue(value, indicatorId, true);
  }

  /**
   * target-workaround for type-safety
   * @param target
   */
  getTarget(target: EventTarget | null): HTMLInputElement {
    return target as HTMLInputElement;
  }

  /**
   * Closes the currently active report by invoking the appropriate method in the global store.
   * This action finalizes the report and performs necessary cleanup.
   *
   * @return {void} No return value.
   */
  closeReport(): void {
    this.globalsStore.closeActiveReport();
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * It performs cleanup activities, such as clearing filters from the indicator store.
   *
   * @return {void} No return value.
   */
  ngOnDestroy(): void {
    this.indicatorStore.clearFilter();
  }

  /**
   * Updates the current data layer if it matches the specified condition.
   *
   * @param {DataLayer} dataLayer - The new data layer to be set.
   * @return {void} This method does not return a value.
   */
  changeDataLayer(dataLayer: DataLayer): void {
    if (dataLayer.parent === this.dataLayerType?._id) {
      this.dataLayer = dataLayer;
      this.dataLayerId = dataLayer._id;
      this.loadReport();
    }
  }

  /**
   * Updates the current data layer type and triggers associated changes.
   *
   * @param {DataLayerType} dataLayerType - The new data layer type to set.
   * @return {void}
   */
  changeDataLayerType(dataLayerType: DataLayerType): void {
    this.dataLayerType = dataLayerType;
    const latestLayer =
      this.layerStore.getLatestNonForecastByParent(dataLayerType._id) ??
      this.layerStore.getLatestByParent(dataLayerType._id);
    this.changeDataLayer(latestLayer);
    this.changeDistrictKey('');
  }

  /**
   * Exports the data by converting it into an Excel file format.
   * The method processes the tree structure, converts the data for export, and names the resulting file.
   *
   * @return {void} Does not return a value.
   */
  export() {
    const rows = this.flattenTree(this.tree);
    const title = this.getReportTitle();
    const titleRow = ['', 'Min.', 'Max.', 'Mittelwert', 'Wert'];
    this.dataStore.convertChartDataToXlsx({
      exportData: [
        {
          title: 'Gebietsbericht',
          rows: [[title], [], titleRow].concat(rows),
        },
      ] as ExportSheet[],
      filename: sanitizeFilename(title),
      source: this.globalsStore.activeCommune()?.name || '',
    });
  }

  /**
   * Flattens a hierarchical tree structure into a two-dimensional array, where each row represents a node in the tree.
   *
   * @param {any[]} tree - The hierarchical tree structure to flatten. Each node in the tree may contain data, a label, and an array of children nodes.
   * @param {string[][]} result - An optional parameter for accumulating the flattened rows during recursive calls. Defaults to an empty array.
   * @param {number} currentLevel - An optional parameter indicating the current depth level in the tree. Defaults to 0.
   * @return {any[]} A two-dimensional array where each internal array represents a node from the tree, containing formatted data and hierarchical tree structure with prefixes based on depth.
   */
  flattenTree(
    tree: any[],
    result: string[][] = [],
    currentLevel = 0
  ): string[][] {
    if (!this.dataLayer) return [];
    for (const item of tree) {
      const rowData = item.data;
      const spacePrefix =
        '    '.repeat(currentLevel) + (currentLevel > 0 ? '└─ ' : '');

      if (item.type !== 'category' && rowData) {
        const row = [
          spacePrefix + (item.data?.label || item.label),
          this.getValueFormatted(
            this.getMinValue(this.dataLayer._id, rowData._id),
            rowData._id
          ),
          this.getValueFormatted(
            this.getMaxValue(this.dataLayer._id, rowData._id),
            rowData._id
          ),
          this.getValueFormatted(this.getAvgValue(rowData._id), rowData._id),
          this.getValueFormatted(this.getValue(rowData._id), rowData._id),
        ];
        result.push(row);
      } else {
        result.push([
          spacePrefix + (item.data?.label || item.label),
          '',
          '',
          '',
          '',
        ]);
      }

      // Recursively process children if they exist
      if (item.children && item.children.length > 0) {
        this.flattenTree(item.children, result, currentLevel + 1);
      }
    }

    return result;
  }
}
